import http from "../helpers/http";
import { getExternalSystemParams } from "../utils/partner";

class UserAPI {
  /**
   * Login user with provided credentials
   * @param {Object} credentials - User login credentials
   * @param {string} credentials.Token - User token
   * @param {string} credentials.ID - User ID
   * @param {string} credentials.Phone - User phone number
   * @returns {Promise<Object>} User data
   */
  async login({ Token, ID, Phone }) {
    try {
      const response = await http.post("Login", {
        Token: "12" + Token,
        ID,
        Phone,
      });

      if (response?.data?.Error !== 0) {
        throw {
          code: response.data.Error,
          mess: response.data.Message,
        };
      }

      return JSON.parse(response?.data?.Data)[0];
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }

  /**
   * Get user data
   * @param {string} userData - User data token
   * @returns {Promise<Object>} User parameters
   */
  async getUserData(userData) {
    try {
      const response = await http.post("Getuserdata", {
        UserData: userData || localStorage.getItem("userToken"),
      });
      return response;
    } catch (error) {
      console.error("Failed to get user data:", error);
      throw error;
    }
  }

  /**
   * Send SMS verification code
   * @param {Object} payload - SMS verification payload
   * @param {string} payload.Phone - Phone number to send code to
   * @param {string} payload.ClaimNumber - Claim number
   * @param {string} payload.FirstName - User's first name
   * @param {string} payload.LastName - User's last name
   * @param {string} payload.Email - User's email
   * @param {boolean} payload.IsFreefitWeb - Whether it's a FreeFit web request
   * @param {number} payload.ExternalSystem - Partner ID (1=Clalit, 2=Mehudet, 3=Leumit, 4=OvdimBari)
   * @returns {Promise<Object>} Response from the server
   */
  async sendSmsCode(payload) {
    try {
      const response = await http.post("SendSmsCode", payload);
      return response;
    } catch (error) {
      console.error("Failed to send SMS code:", error);
      throw error;
    }
  }

  /**
   * Verify SMS code
   * @param {Object} payload - SMS verification payload
   * @param {string} payload.Phone - Phone number to verify
   * @param {string} payload.Code - Verification code
   * @param {string} payload.ClaimNumber - Claim number
   * @param {string} payload.FirstName - User's first name
   * @param {string} payload.LastName - User's last name
   * @param {string} payload.Email - User's email
   * @param {boolean} payload.IsFreefitWeb - Whether it's a FreeFit web request
   * @returns {Promise<Object>} Response from the server
   */
  async verifySmsCode(payload) {
    try {
      const response = await http.post("VerifySmsCode", payload);
      if (response.data?.Error) {
        throw new Error(
          JSON.stringify({
            code: response.data.Error,
            message: response.data.Message,
          }),
        );
      }
      return response;
    } catch (error) {
      console.error("Failed to verify SMS code:", error);
      throw error;
    }
  }

  /**
   * Check if client has a credit card
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing credit card information
   */
  async isClientHaveCreditCard({ token, id, phone }) {
    try {
      const response = await http.post("IsClientHaveCreditCard", {
        Token: token,
        ID: id,
        Phone: phone,
      });
      return response;
    } catch (error) {
      console.error("Failed to check client credit card:", error);
      throw error;
    }
  }

  /**
   * Save or delete client's credit card
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @param {boolean} params.saveCreditCard - Whether to save or delete the credit card
   * @returns {Promise<Object>} Response from the server
   */
  async saveDeleteClientCreditCard({ token, id, phone, saveCreditCard }) {
    try {
      const response = await http.post("SaveDeleteClientCreditCard", {
        Token: token,
        ID: id,
        Phone: phone,
        SaveCreditCard: saveCreditCard,
      });
      return response;
    } catch (error) {
      console.error("Failed to save/delete client credit card:", error);
      throw error;
    }
  }

  /**
   * Update client email by mobile
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @param {string} params.email - New email address
   * @returns {Promise<Object>} Response from the server
   */
  async updateClientEmailByMobile({ token, id, phone, email }) {
    try {
      const response = await http.post("UpdateClientEmailByMobile", {
        Token: token,
        ID: id,
        Phone: phone,
        Email: email,
      });
      return response;
    } catch (error) {
      console.error("Failed to update client email:", error);
      throw error;
    }
  }

  /**
   * Confirm client health condition statement
   * @param {Object} params - Parameters for the request
   * @param {string} params.id - User ID
   * @param {string} params.token - User token
   * @param {string} params.phone - User phone number
   * @param {boolean} params.confirmed - Whether the user confirmed the statement
   * @param {Object} params.answers - Object containing question IDs and their answers
   * @returns {Promise<Object>} Response from the server
   */
  async confirmClientHealthConditionStatement({ id, token, phone, confirmed, answers }) {
    try {
      const response = await http.post("ConfirmClientHealthConditionStatement", {
        ID: id,
        Token: token,
        Phone: phone,
        Confirmed: confirmed,
        QuestionList: Object.entries(answers).map((el) => ({
          ID: el[0],
          Answer: el[1],
        })),
      });
      return response;
    } catch (error) {
      console.error("Failed to confirm client health condition statement:", error);
      throw error;
    }
  }

  /**
   * Delete client's credit card
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response from the server
   */
  async deleteClientCreditCard({ token, id, phone }) {
    try {
      const response = await http.post("DeleteClientCreditCard", {
        Token: token,
        ID: id,
        Phone: phone,
      });
      return response;
    } catch (error) {
      console.error("Failed to delete client credit card:", error);
      throw error;
    }
  }

  /**
   * Confirm client terms of use condition statement
   * @param {Object} params - Parameters for the request
   * @param {string} params.id - User ID
   * @param {string} params.token - User token
   * @param {string} params.phone - User phone number
   * @param {boolean} params.confirmed - Whether the user confirmed the terms
   * @returns {Promise<Object>} Response from the server
   */
  async confirmClientTermOfUseConditionStatement({ id, token, phone, confirmed }) {
    try {
      const response = await http.post("ConfirmClientTermOfUseConditionStatement", {
        ID: id,
        Token: token,
        Phone: phone,
        Confirmed: confirmed,
      });
      return response;
    } catch (error) {
      console.error("Failed to confirm client terms of use:", error);
      throw error;
    }
  }

  /**
   * Purchase a FreeFit web pass
   * @param {Object} params - Parameters for purchasing a pass
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @param {number} params.passPlanId - Pass plan ID
   * @returns {Promise<Object>} Promise containing the purchased pass data
   */
  async purchaseMultipleEntryPass({ token, id, phone, passPlanId }) {
    try {
      const response = await http.post(
        "PurchaseFreeFitWebPass",
        {
          Token: token,
          ID: id,
          Phone: phone,
          PassPlanId: passPlanId,
          ...getExternalSystemParams(),
        },
        { "Idempotency-Key": crypto.randomUUID() }
      );

      if (response?.data?.Error !== 0) {
        throw {
          code: response.data.Error,
          mess: response.data.Message,
        };
      }

      return JSON.parse(response?.data?.Data);
    } catch (error) {
      console.error("Failed to purchase FreeFit web pass:", error);
      throw error;
    }
  }

  /**
   * Get all FreeFit web passes for user
   * @param {Object} params - Parameters for retrieving passes
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Array>} Promise containing user's passes
   */
  async getAllMultipleEntryPass({ token, id, phone }) {
    try {
      const response = await http.post("GetAllFreefitWebPass", {
        Token: token,
        ID: id,
        Phone: phone,
        ...getExternalSystemParams(),
      });

      if (response?.data?.Error !== 0) {
        throw {
          code: response.data.Error,
          mess: response.data.Message,
        };
      }
      return JSON.parse(response?.data?.Data);
    } catch (error) {
      console.error("Failed to get FreeFit web passes:", error);
      throw error;
    }
  }

  /**
   * Check if pass purchase is possible for the user
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {number} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing pass purchase possibility status
   */
  async canPassPurchasePossible({ token, id, phone }) {
    try {
      const response = await http.post("CanPassPurchasePossible", {
        Token: token,
        ID: id,
        Phone: phone,
        ...getExternalSystemParams(),
      });

      if (response?.data?.Error !== 0) {
        throw {
          code: response.data.Error,
          mess: response.data.Message,
        };
      }

      return JSON.parse(response?.data?.Data);
    } catch (error) {
      console.error("Failed to check pass purchase possibility:", error);
      throw error;
    }
  }

  /**
   * Update client's saved location/address
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @param {number} params.latitude - Address latitude
   * @param {number} params.longitude - Address longitude
   * @param {string} params.houseNumber - House number (may be empty; the payload falls back to "1")
   * @param {string} params.city - City name
   * @param {string} params.street - Street name (may be empty; the payload falls back to "-")
   * @returns {Promise<Object>} Response from the server
   */
  async updateClientLocation({ token, id, phone, latitude, longitude, houseNumber, city, street }) {
    try {
      const response = await http.post("UpdateClientLocation", {
        Token: token,
        ID: id,
        Phone: phone,
        Latitude: latitude,
        Longitude: longitude,
        // The backend must never receive an empty street or house number. Payload
        // only: the honest (possibly empty) values stay in the store, and display
        // code treats a "-" street coming back on the Login response as "no street".
        HouseNumber: String(houseNumber ?? "").trim() || "1",
        City: city,
        Street: String(street ?? "").trim() || "-",
      });

      if (response?.data?.Error !== 0) {
        throw {
          code: response.data.Error,
          mess: response.data.Message,
        };
      }
      return response;
    } catch (error) {
      console.error("Failed to update client location:", error);
      throw error;
    }
  }
}

const userAPI = new UserAPI();
export default userAPI;
